/**
 * Words, paragraphs and sentences — always as character offsets into the reader's text.
 *
 * Every text-analysis tool highlights what it found in the text the reader pasted, so
 * nothing here normalises the input: a sentence is a `[start, end)` span, not a cleaned
 * copy, and `text.slice(start, end)` gives back the reader's own characters.
 *
 * Academic prose is hostile to naive sentence splitting. "Smith et al. (2020) found",
 * "(e.g., Lee, 2019)", "p. 45", "J. K. Rowling", "3.14", "the U.S. Census" and a
 * numeric citation after the full stop ("…was significant.[12] The") all put a period
 * where no sentence ends. The rules below are the ones those cases need, and
 * `text-analysis.test.ts` pins each of them.
 */

export interface TextSpan {
  start: number;
  end: number;
}

export interface Token extends TextSpan {
  text: string;
}

export interface Sentence extends TextSpan {
  /** Index of the paragraph the sentence belongs to. */
  paragraph: number;
  /** The first sentence of its paragraph. */
  first: boolean;
  /** A heading-like line (short, no closing punctuation) rather than a prose sentence. */
  heading: boolean;
}

const COUNT_TOKEN_RE = /\S+/g;
const HAS_WORD_CHAR_RE = /[\p{L}\p{N}]/u;
const WORD_RE = /[\p{L}\p{N}]+(?:['’-][\p{L}\p{N}]+)*/gu;
const WHITESPACE_RUN_RE = /\s+/g;

/**
 * The words a word counter counts: runs of non-space characters that contain at least
 * one letter or digit. "e.g.," is one word, "well-known" is one word, a dash standing
 * alone between spaces is none.
 */
export function countWords(text: string): number {
  let count = 0;
  for (const match of text.matchAll(COUNT_TOKEN_RE)) {
    if (HAS_WORD_CHAR_RE.test(match[0])) {
      count++;
    }
  }
  return count;
}

/** Lexical words — letters and digits, with in-word apostrophes and hyphens kept. */
export function wordTokens(text: string): Token[] {
  const tokens: Token[] = [];
  for (const match of text.matchAll(WORD_RE)) {
    tokens.push({
      start: match.index,
      end: match.index + match[0].length,
      text: match[0],
    });
  }
  return tokens;
}

/** A span's text with every whitespace run (line breaks included) collapsed to a space. */
export function collapseWhitespace(text: string): string {
  return text.replace(WHITESPACE_RUN_RE, " ").trim();
}

/* ----------------------------------------------------------------- paragraphs */

/** A line that finishes a paragraph: closing punctuation, quotes, then an optional citation. */
const PARAGRAPH_END_RE = /[.!?…:]["'”’)\]]*(?:\s*\([^()]*\))?$/;
const LIST_ITEM_RE = /^(?:[-*•–]\s|\(?\d{1,2}[.)]\s|\(?[a-z][.)]\s)/;
const STARTS_UPPER_RE = /^["'“‘(]?[\p{Lu}\p{N}]/u;
const HEADING_TERMINAL_RE = /[.,;:]$/;
const HEADING_WORD_LIMIT = 12;

function looksLikeHeading(line: string): boolean {
  return (
    !HEADING_TERMINAL_RE.test(line) &&
    STARTS_UPPER_RE.test(line) &&
    countWords(line) <= HEADING_WORD_LIMIT
  );
}

/**
 * Does a line break between these two lines start a new paragraph?
 *
 * Text pasted from Word or Google Docs separates paragraphs with a single line break;
 * text pasted from a PDF breaks every line. A break counts when the line before it
 * finishes a sentence, when a list item follows, or when the line before is a heading —
 * otherwise it is a soft wrap inside a paragraph.
 */
function breaksParagraph(previous: string, next: string): boolean {
  if (PARAGRAPH_END_RE.test(previous) || LIST_ITEM_RE.test(next)) {
    return true;
  }
  return looksLikeHeading(previous) && STARTS_UPPER_RE.test(next);
}

interface Line extends TextSpan {
  text: string;
}

function trimmedLines(text: string): Line[] {
  const lines: Line[] = [];
  let lineStart = 0;
  for (let i = 0; i <= text.length; i++) {
    if (i < text.length && text[i] !== "\n") {
      continue;
    }
    const raw = text.slice(lineStart, i);
    const lead = raw.length - raw.trimStart().length;
    const content = raw.trim();
    lines.push({
      start: lineStart + lead,
      end: lineStart + lead + content.length,
      text: content,
    });
    lineStart = i + 1;
  }
  return lines;
}

/** Paragraph spans, trimmed. Blank lines always separate paragraphs. */
export function splitParagraphs(text: string): TextSpan[] {
  const paragraphs: TextSpan[] = [];
  let current: TextSpan | null = null;
  let previous = "";
  for (const line of trimmedLines(text)) {
    if (line.text === "") {
      current = null;
      continue;
    }
    if (current && breaksParagraph(previous, line.text)) {
      current = null;
    }
    if (current) {
      current.end = line.end;
    } else {
      current = { start: line.start, end: line.end };
      paragraphs.push(current);
    }
    previous = line.text;
  }
  return paragraphs;
}

/* ------------------------------------------------------------------ sentences */

const SENTENCE_PUNCT_RE = /[.!?…]+/g;
const CLOSERS = new Set(['"', "'", "”", "’", ")", "]", "}", "»"]);
const OPENING_QUOTES = new Set(['"', "“", "‘", "'", "«"]);
const NUMERIC_MARKER_RE =
  /^(?:\[\d{1,3}(?:\s*[,–-]\s*\d{1,3})*\]|[⁰¹²³⁴⁵⁶⁷⁸⁹]+)/;
const TYPED_MARKER_RE = /^\d{1,3}(?=\s|$)/;
const UPPER_RE = /\p{Lu}/u;
const LETTER_RE = /\p{L}/u;
const SPACE_RE = /\s/;
const ABBREVIATION_CHAR_RE = /[\p{L}.]/u;
const SECTION_NUMBER_RE = /^(?:\d{1,3}|[IVXivx]{1,4}|\p{L})$/u;

/**
 * Abbreviations that end in a period but never end a sentence in the prose this tool
 * reads. "et al." is deliberately absent: it ends sentences ("…as reported by Smith et
 * al. The results…"), and the cases where it does not — "et al. (2020)", "et al., 2020"
 * — are already caught because no capital letter follows.
 */
const NON_TERMINAL = new Set([
  "e.g.",
  "i.e.",
  "cf.",
  "vs.",
  "viz.",
  "mr.",
  "mrs.",
  "ms.",
  "dr.",
  "prof.",
  "st.",
  "fig.",
  "figs.",
  "eq.",
  "eqs.",
  "no.",
  "nos.",
  "vol.",
  "vols.",
  "ch.",
  "chap.",
  "sec.",
  "p.",
  "pp.",
  "para.",
  "paras.",
  "ed.",
  "eds.",
  "trans.",
  "approx.",
  "ca.",
  "c.",
  "dept.",
  "univ.",
  "u.s.",
  "u.k.",
  "u.n.",
  "e.u.",
  "rev.",
  "gen.",
  "col.",
  "lt.",
  "sgt.",
  "capt.",
  "gov.",
  "sen.",
  "rep.",
  "hon.",
  "mt.",
]);

/** Move past closing quotes and brackets, then a numeric citation or superscript marker. */
function extendPastClosers(text: string, from: number, end: number): number {
  let k = from;
  while (k < end && CLOSERS.has(text[k])) {
    k++;
  }
  const rest = text.slice(k, end);
  const marker = NUMERIC_MARKER_RE.exec(rest);
  if (marker) {
    return k + marker[0].length;
  }
  // "…was significant.12 The" — a superscript that lost its formatting in the paste.
  const before = text[from - 2] ?? "";
  const typed = TYPED_MARKER_RE.exec(rest);
  if (typed && k === from && LETTER_RE.test(before)) {
    return k + typed[0].length;
  }
  return k;
}

/** Could a sentence start at `n`? A capital, or an opening quote before a capital. */
function startsSentence(text: string, n: number): boolean {
  const c = text[n];
  if (UPPER_RE.test(c)) {
    return true;
  }
  return OPENING_QUOTES.has(c) && UPPER_RE.test(text[n + 1] ?? "");
}

/** Is the period at `dot` part of an abbreviation, an initial, or a list number? */
function isNonTerminal(text: string, paragraphStart: number, dot: number) {
  let wordStart = dot;
  while (
    wordStart > paragraphStart &&
    ABBREVIATION_CHAR_RE.test(text[wordStart - 1])
  ) {
    wordStart--;
  }
  const word = text.slice(wordStart, dot + 1).toLowerCase();
  if (NON_TERMINAL.has(word)) {
    return true;
  }
  // An initial: "J. K. Rowling", "Smith, J. (2020)".
  if (word.length === 2 && UPPER_RE.test(text[wordStart])) {
    return true;
  }
  // "1. Introduction", "IV. Results" at the start of a paragraph.
  let numberStart = dot;
  while (
    numberStart > paragraphStart &&
    !SPACE_RE.test(text[numberStart - 1])
  ) {
    numberStart--;
  }
  return (
    numberStart === paragraphStart &&
    SECTION_NUMBER_RE.test(text.slice(numberStart, dot))
  );
}

function trimEnd(text: string, start: number, end: number): number {
  let e = end;
  while (e > start && SPACE_RE.test(text[e - 1])) {
    e--;
  }
  return e;
}

interface Boundary {
  /** Where the finished sentence ends. */
  end: number;
  /** Where the next one starts. */
  next: number;
}

/** The sentence boundary that the punctuation run at `[at, after)` closes, if any. */
function boundaryAt(
  text: string,
  paragraph: TextSpan,
  at: number,
  after: number
): Boundary | null {
  const end = extendPastClosers(text, after, paragraph.end);
  if (end < paragraph.end && !SPACE_RE.test(text[end])) {
    return null;
  }
  let next = end;
  while (next < paragraph.end && SPACE_RE.test(text[next])) {
    next++;
  }
  if (next >= paragraph.end || !startsSentence(text, next)) {
    return null;
  }
  const single = after - at === 1 && text[at] === ".";
  if (single && isNonTerminal(text, paragraph.start, at)) {
    return null;
  }
  return { end, next };
}

/**
 * A parenthesis or bracket left open this close to a period keeps the sentence going:
 * "(Smith et al. 2020, 45)" is one citation, not two sentences. Further away than this,
 * the bracket is taken to be a typo that never closes, so one stray "(" cannot stop
 * sentence splitting for the rest of the paragraph.
 */
const OPEN_BRACKET_REACH = 250;

/** Track open brackets between `from` and `to`; the stack holds opener offsets. */
function advanceBrackets(
  text: string,
  from: number,
  to: number,
  open: number[]
) {
  for (let i = from; i < to; i++) {
    const c = text[i];
    if (c === "(" || c === "[") {
      open.push(i);
    } else if ((c === ")" || c === "]") && open.length > 0) {
      open.pop();
    }
  }
}

function insideBrackets(open: number[], at: number): boolean {
  const last = open.at(-1);
  if (last === undefined) {
    return false;
  }
  if (at - last <= OPEN_BRACKET_REACH) {
    return true;
  }
  open.length = 0;
  return false;
}

function sentencesInParagraph(text: string, paragraph: TextSpan): TextSpan[] {
  const spans: TextSpan[] = [];
  let sentenceStart = paragraph.start;
  const open: number[] = [];
  let scanned = paragraph.start;
  const body = text.slice(paragraph.start, paragraph.end);
  for (const match of body.matchAll(SENTENCE_PUNCT_RE)) {
    const at = paragraph.start + match.index;
    advanceBrackets(text, scanned, at, open);
    scanned = at;
    if (insideBrackets(open, at)) {
      continue;
    }
    const boundary = boundaryAt(text, paragraph, at, at + match[0].length);
    if (!boundary) {
      continue;
    }
    spans.push({ start: sentenceStart, end: boundary.end });
    sentenceStart = boundary.next;
  }
  spans.push({
    start: sentenceStart,
    end: trimEnd(text, sentenceStart, paragraph.end),
  });
  return spans.filter((span) => span.end > span.start);
}

const SENTENCE_FINAL_RE = /[.!?…]["'”’)\]]*$/;

/** Sentences, in reading order, with their paragraph and a heading flag. */
export function splitSentences(text: string): Sentence[] {
  const sentences: Sentence[] = [];
  for (const [index, paragraph] of splitParagraphs(text).entries()) {
    const spans = sentencesInParagraph(text, paragraph);
    for (const [position, span] of spans.entries()) {
      const content = text.slice(span.start, span.end);
      sentences.push({
        ...span,
        paragraph: index,
        first: position === 0,
        heading:
          spans.length === 1 &&
          !SENTENCE_FINAL_RE.test(content) &&
          countWords(content) <= HEADING_WORD_LIMIT,
      });
    }
  }
  return sentences;
}
